import { useState } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import { IconCheck } from '@/components/icons/Icons'
import type { Cliente, ConfigTarifaCliente, TipoDocumentoFiscal } from '@/lib/types'

interface TabConfiguracionProps {
  empresa: Cliente
  tarifaConfig: ConfigTarifaCliente | null
  onUpdate: () => void
}

export default function TabConfiguracion({ empresa, tarifaConfig, onUpdate }: TabConfiguracionProps) {
  const { update, add } = useSupabase()

  // Fiscal data form
  const [tipoDoc, setTipoDoc] = useState<TipoDocumentoFiscal>(empresa.tipo_documento_fiscal || 'RUC')
  const [rucDni, setRucDni] = useState(empresa.ruc_dni || '')
  const [razonSocial, setRazonSocial] = useState(empresa.razon_social || '')
  const [dirFiscal, setDirFiscal] = useState(empresa.direccion_fiscal || '')
  const [ubigeo, setUbigeo] = useState(empresa.ubigeo || '')

  // Tarifa form
  const [tarifaHora, setTarifaHora] = useState(tarifaConfig?.tarifa_hora ?? 0)
  const [umbralHoras, setUmbralHoras] = useState(tarifaConfig?.umbral_horas_mes ?? 0)
  const [moneda, setMoneda] = useState(tarifaConfig?.moneda ?? 'USD')

  const [savingFiscal, setSavingFiscal] = useState(false)
  const [savingTarifa, setSavingTarifa] = useState(false)

  const esExtranjero = tipoDoc === 'TAX_ID'

  async function handleSaveFiscal() {
    setSavingFiscal(true)
    try {
      await update('clientes', {
        id: empresa.id,
        tipo_documento_fiscal: tipoDoc,
        ruc_dni: rucDni,
        razon_social: razonSocial,
        direccion_fiscal: dirFiscal,
        ubigeo: esExtranjero ? null : ubigeo,
      })
      showToast('Datos fiscales actualizados', 'success')
      onUpdate()
    } catch {
      showToast('Error al actualizar datos fiscales', 'error')
    } finally {
      setSavingFiscal(false)
    }
  }

  async function handleSaveTarifa() {
    setSavingTarifa(true)
    try {
      if (tarifaConfig) {
        await update('config_tarifa_cliente', {
          id: tarifaConfig.id,
          tarifa_hora: tarifaHora,
          umbral_horas_mes: umbralHoras,
          moneda,
        })
      } else {
        await add('config_tarifa_cliente', {
          cliente_id: empresa.id,
          tarifa_hora: tarifaHora,
          umbral_horas_mes: umbralHoras,
          moneda,
        })
      }
      showToast('Tarifa actualizada', 'success')
      onUpdate()
    } catch {
      showToast('Error al actualizar tarifa', 'error')
    } finally {
      setSavingTarifa(false)
    }
  }

  return (
    <div className="empresa-config-sections">
      {/* Datos Fiscales */}
      <div className="empresa-section-card">
        <h4 className="empresa-section-title">Datos Fiscales</h4>
        <div className="empresa-config-form">
          <div className="form-group">
            <label className="form-label">Tipo de Documento</label>
            <select
              className="form-select"
              value={tipoDoc}
              onChange={e => setTipoDoc(e.target.value as TipoDocumentoFiscal)}
            >
              <option value="RUC">RUC</option>
              <option value="DNI">DNI</option>
              <option value="CE">CE</option>
              <option value="TAX_ID">Tax ID (Extranjero)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{esExtranjero ? 'Tax ID' : 'N de Documento'}</label>
            <input
              className="form-input"
              value={rucDni}
              onChange={e => setRucDni(e.target.value)}
              placeholder={esExtranjero ? 'Tax ID' : tipoDoc === 'RUC' ? '20XXXXXXXXX' : 'XXXXXXXX'}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Razon Social</label>
            <input
              className="form-input"
              value={razonSocial}
              onChange={e => setRazonSocial(e.target.value)}
              placeholder="Razon social o nombre legal"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Direccion Fiscal</label>
            <input
              className="form-input"
              value={dirFiscal}
              onChange={e => setDirFiscal(e.target.value)}
              placeholder="Direccion fiscal"
            />
          </div>

          {!esExtranjero && (
            <div className="form-group">
              <label className="form-label">Ubigeo</label>
              <input
                className="form-input"
                value={ubigeo}
                onChange={e => setUbigeo(e.target.value)}
                placeholder="150101"
                maxLength={6}
              />
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleSaveFiscal}
            disabled={savingFiscal}
            type="button"
          >
            {savingFiscal ? (
              <div className="auth-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
            ) : (
              <IconCheck size={14} />
            )}
            Guardar Datos Fiscales
          </button>
        </div>
      </div>

      {/* Tarifa de Proyecto */}
      <div className="empresa-section-card">
        <h4 className="empresa-section-title">Tarifa de Proyecto</h4>
        <div className="empresa-config-form">
          <div className="form-group">
            <label className="form-label">Tarifa por Hora</label>
            <input
              className="form-input"
              type="number"
              min={0}
              step={0.01}
              value={tarifaHora}
              onChange={e => setTarifaHora(Number(e.target.value))}
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Umbral Horas/Mes</label>
            <input
              className="form-input"
              type="number"
              min={0}
              value={umbralHoras}
              onChange={e => setUmbralHoras(Number(e.target.value))}
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Moneda</label>
            <select
              className="form-select"
              value={moneda}
              onChange={e => setMoneda(e.target.value)}
            >
              <option value="USD">USD</option>
              <option value="PEN">PEN</option>
              <option value="EUR">EUR</option>
            </select>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSaveTarifa}
            disabled={savingTarifa}
            type="button"
          >
            {savingTarifa ? (
              <div className="auth-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
            ) : (
              <IconCheck size={14} />
            )}
            Guardar Tarifa
          </button>
        </div>
      </div>
    </div>
  )
}
